import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'

import { db, type MusicPlaylist } from '@/lib/db'

/**
 * プレイリスト CRUD ロジックを管理するフック
 */
export const usePlaylist = () => {
  const [playlists, setPlaylists] = useState<MusicPlaylist[]>([])

  const loadPlaylists = useCallback(async () => {
    const all = await db.musicPlaylists.toArray()
    all.sort((a, b) => a.order - b.order)
    setPlaylists(all)
  }, [])

  useEffect(() => {
    loadPlaylists()
  }, [loadPlaylists])

  /** プレイリストを新規作成 */
  const createPlaylist = useCallback(
    async (name: string) => {
      const trimmed = name.trim()
      if (!trimmed) return

      const existing = await db.musicPlaylists.toArray()
      const maxOrder = existing.length > 0 ? Math.max(...existing.map((p) => p.order)) : -1

      await db.musicPlaylists.add({
        id: crypto.randomUUID(),
        name: trimmed,
        musicIds: [],
        createdAt: Date.now(),
        updatedAt: Date.now(),
        order: maxOrder + 1,
      })

      toast.success(`「${trimmed}」を作成しました`)
      await loadPlaylists()
    },
    [loadPlaylists],
  )

  /** プレイリスト名を変更 */
  const renamePlaylist = useCallback(
    async (id: string, newName: string) => {
      const trimmed = newName.trim()
      if (!trimmed) return

      await db.musicPlaylists.update(id, { name: trimmed, updatedAt: Date.now() })
      toast.success('名前を変更しました')
      await loadPlaylists()
    },
    [loadPlaylists],
  )

  /** プレイリストを削除（曲自体は消さない） */
  const deletePlaylist = useCallback(
    async (id: string) => {
      await db.musicPlaylists.delete(id)
      toast.success('プレイリストを削除しました')
      await loadPlaylists()
    },
    [loadPlaylists],
  )

  /** 曲をプレイリストに追加 */
  const addMusicToPlaylist = useCallback(
    async (playlistId: string, musicId: string) => {
      const playlist = await db.musicPlaylists.get(playlistId)
      if (!playlist) return
      if (playlist.musicIds.includes(musicId)) return

      await db.musicPlaylists.update(playlistId, {
        musicIds: [...playlist.musicIds, musicId],
        updatedAt: Date.now(),
      })
      await loadPlaylists()
    },
    [loadPlaylists],
  )

  /** 曲をプレイリストから除外 */
  const removeMusicFromPlaylist = useCallback(
    async (playlistId: string, musicId: string) => {
      const playlist = await db.musicPlaylists.get(playlistId)
      if (!playlist) return

      await db.musicPlaylists.update(playlistId, {
        musicIds: playlist.musicIds.filter((id) => id !== musicId),
        updatedAt: Date.now(),
      })
      await loadPlaylists()
    },
    [loadPlaylists],
  )

  /** プレイリスト内の曲順を更新 */
  const reorderPlaylistMusic = useCallback(
    async (playlistId: string, newMusicIds: string[]) => {
      await db.musicPlaylists.update(playlistId, {
        musicIds: newMusicIds,
        updatedAt: Date.now(),
      })
      await loadPlaylists()
    },
    [loadPlaylists],
  )

  /** 曲が所属するプレイリストIDのSetを返す */
  const getPlaylistIdsForMusic = useCallback(
    (musicId: string): Set<string> => {
      const ids = new Set<string>()
      for (const pl of playlists) {
        if (pl.musicIds.includes(musicId)) {
          ids.add(pl.id)
        }
      }
      return ids
    },
    [playlists],
  )

  return {
    playlists,
    loadPlaylists,
    createPlaylist,
    renamePlaylist,
    deletePlaylist,
    addMusicToPlaylist,
    removeMusicFromPlaylist,
    reorderPlaylistMusic,
    getPlaylistIdsForMusic,
  }
}
